// covered-by: packages/schemas/src/christo/__tests__/catalyst.test.ts
import { extendZodWithOpenApi } from "@asteasolutions/zod-to-openapi/dist/zod-extensions";
import { z } from "zod";

import { enterpriseSlugSchema } from "./enterpriseSlug";

import {
  assetSchema,
  enterpriseBriefGuidelines,
  enterpriseProfileTypeSchema,
  enterpriseProfileSchema,
  enterpriseSchema,
  userSchema,
  teamSchema,
  workflowSchema,
  enterpriseAIPersonas,
  enterpriseAskPunttSkillSchema,
  enterpriseAskPunttConnectorSchema,
  enterpriseCustomWorkflowSchema,
  enterpriseWarholOutputSchema,
  enterpriseAIPersonasUpdate,
  UserType,
} from "../commons";
import {
  folderSchema,
  folderTreeSchema,
  workflowResultSchema,
} from "../commons/folder";
import {
  KnowledgeScopeSource,
  KnowledgeSourceType,
  KnowledgeTrustLevel,
  knowledgeCitationLocationSchema,
  knowledgeScopeSchema,
} from "../commons/knowledge";
import {
  KnowledgeActivityAction,
  KnowledgeActivityActorKind,
  KnowledgeActivityTargetType,
} from "../commons/knowledgeActivity";
import {
  KnowledgeRuleCategory,
  knowledgeRuleClaimSchema,
  KnowledgeRuleOrigin,
  KnowledgeRuleStatus,
  knowledgeRuleTagsSchema,
} from "../commons/knowledgeRule";
import {
  KnowledgeExtractionStatus,
  KnowledgeSourceStatus,
} from "../commons/knowledgeSource";
import {
  TicketCommentBoard,
  TicketCommentDisposition,
  TicketMessageRating,
  boardSchema,
  fileCoordSchema,
  redlinesSchema,
  revisionBoardSchema,
  ticketCommentSchema,
  ticketMessageCitationSchema,
  ticketMessageSchema,
  ticketMessageVoteSchema,
  ticketParticipantsSchema,
  ticketReviewSchema,
  ticketSchema,
  ticketTemplateSchema,
} from "../commons/ticket";

export { enterpriseSlugSchema };

extendZodWithOpenApi(z);

export const baseTicketMessageSchema = ticketMessageSchema.extend({
  _id: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  publishedAt: z.string().datetime().optional(),
});

export type BaseTicketMessage = z.infer<typeof baseTicketMessageSchema>;

export const baseTicketCommentSchema = ticketCommentSchema.extend({
  _id: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  publishedAt: z.string().datetime().optional(),
  messages: z.array(baseTicketMessageSchema),
  unlinkedAt: z.string().datetime().optional(),
  resolvedAt: z.string().datetime().optional(),
  resolvedBy: z.string().optional(),
  likedBy: z.string().optional(),
  dismissedBy: z.string().optional(),
});

export type BaseTicketComment = z.infer<typeof baseTicketCommentSchema>;

const baseTicketReviewSchema = ticketReviewSchema.extend({
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type BaseTicketReview = z.infer<typeof baseTicketReviewSchema>;

const baseRevisionBoardSchema = revisionBoardSchema.extend({
  _id: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  deletedAt: z.string().datetime().optional(),
  reviews: z.array(baseTicketReviewSchema),
});

export type BaseRevisionBoard = z.infer<typeof baseRevisionBoardSchema>;

export const baseBoardSchema = boardSchema.extend({
  _id: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type BaseBoard = z.infer<typeof baseBoardSchema>;

export const baseWorkflowSchema = workflowSchema.extend({
  _id: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type BaseWorkflow = z.infer<typeof baseWorkflowSchema>;

export const baseTicketSchema = ticketSchema
  .extend({
    _id: z.string(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    briefBoard: baseBoardSchema,
    revisionBoards: z.array(baseRevisionBoardSchema),
    workflow: baseWorkflowSchema,
    thumbnails: z.array(z.string()).default([]),
    isPending: z.boolean().default(false),
    totalFiles: z.number().default(0),
    documentImportType: z.string().optional(),
    totalRevisions: z.number().default(0),
    markup: z.string().optional(),
  })
  .omit({ comments: true });

export type BaseTicket = z.infer<typeof baseTicketSchema>;

export const getTicketsPaginatedBodySchema = z.object({
  // validate searchAfter to be "timestamp,_id"
  searchAfter: z.string().optional(),
  ticketTitle: z.string().optional(),
  participants: z.array(z.string()).optional(),
  workflowSteps: z.array(z.number()).optional(),
  completedOnly: z.boolean().optional(),
  pageSize: z.coerce.number().default(100).optional(),
});
export type GetTicketsPaginatedBody = z.infer<
  typeof getTicketsPaginatedBodySchema
>;

export const getTicketsPaginatedRequest = z.object({
  body: getTicketsPaginatedBodySchema,
});

export type GetTicketsPaginatedRequest = z.infer<
  typeof getTicketsPaginatedRequest
>;

export const getFolderTicketsPaginatedBodySchema =
  getTicketsPaginatedBodySchema.extend({
    folderId: z.string().optional(),
    query: z.string().optional(),
  });

export const getFoldersResponseSchema = z.array(folderSchema);

export type GetFolderTicketsPaginatedBody = z.infer<
  typeof getFolderTicketsPaginatedBodySchema
>;

export const getFolderTicketsPaginatedRequest = z.object({
  body: getFolderTicketsPaginatedBodySchema,
});

export type GetFolderTicketsPaginatedRequest = z.infer<
  typeof getFolderTicketsPaginatedRequest
>;

export const responseFolderSchema = folderSchema
  .omit({
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    _id: z.string(),
    thumbnails: z.array(z.string()).default([]),
    totalFiles: z.number().default(0),
    participants: ticketParticipantsSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
    isAiReviewed: z.boolean().optional(),
    aiPersonas: z.array(z.string()).optional(),
  });

export type ResponseFolder = z.infer<typeof responseFolderSchema>;

export const getFolderParamsSchema = z.object({
  folderId: z.string(),
});

export const getBreadcrumbsResponseSchema = z.array(
  z.object({
    _id: z.string(),
    name: z.string(),
  }),
);

export type GetBreadcrumbsResponse = z.infer<
  typeof getBreadcrumbsResponseSchema
>;

export const getFolderTreeResponseSchema = z.array(folderTreeSchema);

export const getTicketsResponseSchema = z.array(
  baseTicketSchema
    .pick({
      _id: true,
      title: true,
      description: true,
      enterprise: true,
      createdAt: true,
      participants: true,
      creatives: true,
      updatedAt: true,
      updatedIndex: true,
      isPending: true,
      thumbnails: true,
      totalFiles: true,
      documentImportType: true,
      totalRevisions: true,
    })
    .extend({
      // Folder fields: always undefined, but helpful for methods that process both folders and tickets
      isFolder: z.boolean().optional(),
      isAiReviewed: z.boolean().optional(),
      aiPersonas: z.array(z.string()).optional(),
    }),
);

export const getFolderTicketsPaginatedResponseSchema = z.object({
  tickets: getTicketsResponseSchema.default([]),
  folders: z.array(responseFolderSchema).optional(),
  more: z.boolean().optional(),
  thumbnails: z.array(z.string()).optional(),
});

export type GetFolderTicketsPaginatedResponse = z.infer<
  typeof getFolderTicketsPaginatedResponseSchema
>;

export const getFolderDetailsResponseSchema = folderSchema
  .pick({
    _id: true,
    name: true,
    createdAt: true,
    updatedAt: true,
    personas: true,
  })
  .extend({
    workflowResults: z.array(workflowResultSchema).default([]),
    tickets: z
      .array(
        baseTicketSchema
          .pick({
            _id: true,
            title: true,
            thumbnails: true,
          })
          .extend({
            lastRevisionBoard: baseRevisionBoardSchema,
          }),
      )
      .optional(),
  });

export type GetFolderDetailsResponse = z.infer<
  typeof getFolderDetailsResponseSchema
>;

export type GetTicketsResponse = z.infer<typeof getTicketsResponseSchema>;

export const getTicketsPaginatedResponseSchema = z.object({
  tickets: getTicketsResponseSchema,
  more: z.boolean(),
});

export type GetTicketsPaginatedResponse = z.infer<
  typeof getTicketsPaginatedResponseSchema
>;

export const getTicketParamsSchema = z.object({
  ticketId: z.string(),
});

export type GetTicketParams = z.infer<typeof getTicketParamsSchema>;

export const getTicketRequest = z.object({
  params: getTicketParamsSchema,
});

export type GetTicketRequest = z.infer<typeof getTicketRequest>;

export const getTicketResponseSchema = baseTicketSchema;

export type GetTicketResponse = z.infer<typeof getTicketResponseSchema>;

/**
 * Share-link cross-enterprise client payloads.
 * Deliberately lean: display name of the *target* workspace only (never
 * enterprise/ticket ids). Current workspace comes from the caller's session.
 */
/** AI dogfood accounts must not follow share links into another workspace. */
export const aiAccountCrossEnterpriseResponseSchema = z.object({
  code: z.literal("ai_account_cross_enterprise"),
  /** Present only when the share target resolved to a real enterprise. */
  targetEnterpriseName: z.string().min(1).optional(),
});

export type AiAccountCrossEnterpriseResponse = z.infer<
  typeof aiAccountCrossEnterpriseResponseSchema
>;

/**
 * Logged-in user is not a member of the share-link target enterprise.
 * Client must confirm before we add a requester profile + allowlist.
 */
export const enterpriseJoinRequiredResponseSchema = z.object({
  code: z.literal("enterprise_join_required"),
  targetEnterpriseName: z.string().min(1),
  resource: z.enum(["ticket", "folder"]),
});

export type EnterpriseJoinRequiredResponse = z.infer<
  typeof enterpriseJoinRequiredResponseSchema
>;

export const createTicketBodySchema = ticketSchema
  .pick({
    title: true,
    description: true,
    briefFiles: true,
    folder: true,
    markup: true,
  })
  .extend({
    teamId: z.string().optional(),
  });

export type CreateTicketBody = z.infer<typeof createTicketBodySchema>;

export const createTicketRequest = z.object({
  body: createTicketBodySchema,
});

export type CreateTicketRequest = z.infer<typeof createTicketRequest>;

export const createTicketResponseSchema = baseTicketSchema;

export type CreateTicketResponse = z.infer<typeof createTicketResponseSchema>;

export const createFolderBodySchema = z.object({
  folder: z.object({
    name: z.string(),
    folderId: z.string().optional(),
    personas: z.array(z.string()).optional(),
  }),
  tickets: z.array(createTicketBodySchema).optional(),
});

export type CreateFolderBody = z.infer<typeof createFolderBodySchema>;

export const CreateFolderRequest = z.object({
  body: createFolderBodySchema,
});

export type CreateFolderRequest = z.infer<typeof CreateFolderRequest>;

export const createFolderResponseSchema = z.object({
  folder: folderSchema,
  tickets: z.array(z.string()),
});

export type CreateFolderResponse = z.infer<typeof createFolderResponseSchema>;

export const updateFolderBodySchema = z.object({
  name: z.string().optional(),
  parentFolder: z.union([z.string(), z.literal(false)]).optional(),
});

export type UpdateFolderBody = z.infer<typeof updateFolderBodySchema>;

export const updateFolderParamsSchema = z.object({
  folderId: z.string(),
});

export type UpdateFolderParams = z.infer<typeof updateFolderParamsSchema>;

export const updateFolderResponseSchema = folderSchema;

export type UpdateFolderResponse = z.infer<typeof updateFolderResponseSchema>;

export const updateTicketBodySchema = ticketSchema
  .pick({
    _id: true,
    description: true,
    files: true,
    moodBoard: true,
    briefBoard: true,
    status: true,
  })
  .extend({
    workflowStep: z.coerce.number().optional(),
    title: z.string().optional(),
    folder: z.union([z.string(), z.literal(false)]).optional(),
  });

export type UpdateTicketBody = z.infer<typeof updateTicketBodySchema>;

export const updateTicketRequest = z.object({
  body: updateTicketBodySchema,
});

export type UpdateTicketRequest = z.infer<typeof updateTicketRequest>;

export const updateTicketResponseSchema = baseTicketSchema;

export type UpdateTicketResponse = z.infer<typeof updateTicketResponseSchema>;

export const archiveTicketParamsSchema = z.object({
  ticketId: z.string(),
});

export type ArchiveTicketParams = z.infer<typeof archiveTicketParamsSchema>;

export const archiveTicketRequest = z.object({
  params: archiveTicketParamsSchema,
});

export type ArchiveTicketRequest = z.infer<typeof archiveTicketRequest>;

export const archiveTicketResponseSchema = baseTicketSchema;

export type ArchiveTicketResponse = z.infer<typeof archiveTicketResponseSchema>;

export const downloadFolderParamsSchema = z.object({
  folderId: z.string(),
});

export type DownloadFolderParams = z.infer<typeof downloadFolderParamsSchema>;

export const downloadFolderRequest = z.object({
  params: downloadFolderParamsSchema,
});

export type DownloadFolderRequest = z.infer<typeof downloadFolderRequest>;

export const downloadFolderResponseSchema = z.object({
  url: z.string(),
});

export type DownloadFolderResponse = z.infer<
  typeof downloadFolderResponseSchema
>;

export const downloadTicketParamsSchema = z.object({
  ticketId: z.string(),
});

export type DownloadTicketParams = z.infer<typeof downloadTicketParamsSchema>;

export const downloadTicketRequest = z.object({
  params: downloadTicketParamsSchema,
});

export type DownloadTicketRequest = z.infer<typeof downloadTicketRequest>;

export const downloadTicketResponseSchema = z.object({
  url: z.string(),
});

export type DownloadTicketResponse = z.infer<
  typeof downloadTicketResponseSchema
>;

// Get Brief Templates

export const getBriefTemplatesResponseSchema = z.array(ticketTemplateSchema);

export type GetBriefTemplatesResponse = z.infer<
  typeof getBriefTemplatesResponseSchema
>;

// generate mood board images
export const generateMoodBoardImagesBodySchema = z.object({
  ticketId: z.string().optional(),
  description: z.string().optional(),
});

export type GenerateMoodBoardImagesBody = z.infer<
  typeof generateMoodBoardImagesBodySchema
>;

export const generateMoodBoardImagesRequest = z.object({
  body: generateMoodBoardImagesBodySchema,
});

export type GenerateMoodBoardImagesRequest = z.infer<
  typeof generateMoodBoardImagesRequest
>;

export const generateMoodBoardImagesResponseSchema = z.object({
  images: z.array(assetSchema),
});

export type GenerateMoodBoardImagesResponse = z.infer<
  typeof generateMoodBoardImagesResponseSchema
>;

// Comments endpoint

export const createTicketCommentBodySchema = z.object({
  commentId: z.string().optional(),
  description: z.string(),
  x: z.coerce.number().optional(),
  y: z.coerce.number().optional(),
  rel_x: z.coerce.number().optional(),
  rel_y: z.coerce.number().optional(),
  page_num: z.coerce.number().optional(),
  videoStart: z.coerce.number().optional(),
  videoEnd: z.coerce.number().optional(),
  aiRating: z.nativeEnum(TicketMessageRating).optional(),
  isPending: z.boolean().default(false).optional(),
  isRequired: z.boolean().default(false).optional(),
  boardType: z.nativeEnum(TicketCommentBoard).optional(),
  boardId: z.string().optional(),
  shapeId: z.string().optional(),
  isReview: z.boolean().default(false).optional(),
  mentions: z.array(z.string()).optional(),
  citations: z.array(ticketMessageCitationSchema).optional(),
  votes: z.array(ticketMessageVoteSchema).optional(),
  rule: z.string().optional(),
  atomic_task: z.string().optional(),
  disposition: z
    .nativeEnum(TicketCommentDisposition)
    .default(TicketCommentDisposition.DEFAULT)
    .optional(),
  liked: z.boolean().nullable().optional(),
  docCommentId: z.string().optional(),
});
export type CreateTicketCommentBody = z.infer<
  typeof createTicketCommentBodySchema
>;

export const createTicketCommentParamsSchema = z.object({
  ticketId: z.string(),
});

export const createTicketCommentRequest = z.object({
  body: createTicketCommentBodySchema,
  params: createTicketCommentParamsSchema,
});

export type CreateTicketCommentRequest = z.infer<
  typeof createTicketCommentRequest
>;

export const createTicketCommentResponseSchema = baseTicketCommentSchema;

export type CreateTicketCommentResponse = z.infer<
  typeof createTicketCommentResponseSchema
>;

export const getTicketCommentsQuerySchema = z.object({
  boardType: z.nativeEnum(TicketCommentBoard).optional(),
});

export type GetTicketCommentsQuery = z.infer<
  typeof getTicketCommentsQuerySchema
>;

export const getTicketCommentsParamsSchema = z.object({
  ticketId: z.string(),
});

export type GetTicketCommentsParams = z.infer<
  typeof getTicketCommentsParamsSchema
>;

export const getTicketCommentsRequest = z.object({
  params: getTicketCommentsParamsSchema,
  query: getTicketCommentsQuerySchema,
});

export type GetTicketCommentsRequest = z.infer<typeof getTicketCommentsRequest>;

export const getTicketCommentsResponseSchema = z.array(baseTicketCommentSchema);

export type GetTicketCommentsResponse = z.infer<
  typeof getTicketCommentsResponseSchema
>;

export const updateTicketCommentBodySchema = createTicketCommentBodySchema.omit(
  { commentId: true },
);

export type UpdateTicketCommentBody = z.infer<
  typeof updateTicketCommentBodySchema
>;

export const updateTicketCommentParamsSchema = z.object({
  ticketId: z.string(),
  messageId: z.string(),
});

export type UpdateTicketCommentParams = z.infer<
  typeof updateTicketCommentParamsSchema
>;

export const updateTicketCommentRequest = z.object({
  body: updateTicketCommentBodySchema,
  params: updateTicketCommentParamsSchema,
});

export type UpdateTicketCommentRequest = z.infer<
  typeof updateTicketCommentRequest
>;

export const updateTicketCommentResponseSchema = baseTicketCommentSchema;

export type UpdateTicketCommentResponse = z.infer<
  typeof updateTicketCommentResponseSchema
>;

export const voteTicketCommentParamsSchema = z.object({
  ticketId: z.string(),
  messageId: z.string(),
});

export type VoteTicketCommentParams = z.infer<
  typeof voteTicketCommentParamsSchema
>;

export const voteTicketCommentBodySchema = z.object({
  vote: z
    .nativeEnum(TicketMessageRating)
    .default(TicketMessageRating.THUMBS_UP),
});

export type VoteTicketCommentBody = z.infer<typeof voteTicketCommentBodySchema>;

export const voteTicketCommentRequest = z.object({
  params: voteTicketCommentParamsSchema,
  body: voteTicketCommentBodySchema,
});

export type VoteTicketCommentRequest = z.infer<typeof voteTicketCommentRequest>;

export const voteTicketCommentResponseSchema = baseTicketCommentSchema;

export type VoteTicketCommentResponse = z.infer<
  typeof voteTicketCommentResponseSchema
>;

export const deleteTicketCommentParamsSchema = z.object({
  ticketId: z.string(),
  messageId: z.string(),
});

export type DeleteTicketCommentParams = z.infer<
  typeof deleteTicketCommentParamsSchema
>;

export const deleteTicketCommentRequest = z.object({
  params: deleteTicketCommentParamsSchema,
});

export type DeleteTicketCommentRequest = z.infer<
  typeof deleteTicketCommentRequest
>;

// Revision endpoints
export const getRevisionsResponseSchema = z.array(baseRevisionBoardSchema);

export type GetRevisionsResponse = z.infer<typeof getRevisionsResponseSchema>;

export const getRevisionStatusResponseSchema = revisionBoardSchema
  .pick({
    isPending: true,
    isAIReady: true,
  })
  .extend({
    errorDetails: z.string().optional(),
  });

export type GetRevisionStatusResponse = z.infer<
  typeof getRevisionStatusResponseSchema
>;

export const updateRevisionBoardBodySchema = revisionBoardSchema.pick({
  _id: true,
  shapes: true,
  screenshotUrl: true,
  version: true,
});

export type UpdateRevisionBoardBody = z.infer<
  typeof updateRevisionBoardBodySchema
>;

export const updateRevisionBoardParams = z.object({
  ticketId: z.string(),
});

export type UpdateRevisionBoardsParams = z.infer<
  typeof updateRevisionBoardParams
>;

export const updateRevisionBoardRequest = z.object({
  body: updateRevisionBoardBodySchema,
  params: updateRevisionBoardParams,
});

export type UpdateRevisionBoardRequest = z.infer<
  typeof updateRevisionBoardRequest
>;

export const updateRevisionBoardResponseSchema = baseRevisionBoardSchema;

export type UpdateRevisionBoardsResponse = z.infer<
  typeof updateRevisionBoardResponseSchema
>;

export const softDeleteRevisionBoardParamsSchema = z.object({
  ticketId: z.string(),
});

export type SoftDeleteRevisionBoardParams = z.infer<
  typeof softDeleteRevisionBoardParamsSchema
>;

export const softDeleteRevisionBoardRequest = z.object({
  params: softDeleteRevisionBoardParamsSchema,
  body: z.object({
    boardId: z.string(),
  }),
});

export type SoftDeleteRevisionBoardRequest = z.infer<
  typeof softDeleteRevisionBoardRequest
>;

export const softDeleteRevisionBoardResponseSchema = z.object({
  boards: z.array(baseRevisionBoardSchema),
});

export type SoftDeleteRevisionBoardResponse = z.infer<
  typeof softDeleteRevisionBoardResponseSchema
>;

export const addBoardToRevisionBoardBodySchema = z.object({
  name: z.string().optional(),
  reviewFiles: z.array(assetSchema),
  assetContext: z.string().optional(),
  contextFiles: z.array(assetSchema).optional(),
  workingFiles: z.array(assetSchema).optional(), // @deprecated
  documentImportType: z.string().default(""),
});

export type AddBoardToRevisionBoardBody = z.infer<
  typeof addBoardToRevisionBoardBodySchema
>;

export const addBoardToRevisionBoardParamsSchema = z.object({
  ticketId: z.string(),
});

export type AddBoardToRevisionBoardParams = z.infer<
  typeof addBoardToRevisionBoardParamsSchema
>;

export const addBoardToRevisionBoardRequest = z.object({
  body: addBoardToRevisionBoardBodySchema,
  params: addBoardToRevisionBoardParamsSchema,
});

export type AddBoardToRevisionBoardRequest = z.infer<
  typeof addBoardToRevisionBoardRequest
>;

export const addBoardToRevisionBoardResponseSchema = z.object({
  boards: z.array(baseRevisionBoardSchema),
  reviewFiles: z.array(fileCoordSchema),
});

export type AddBoardToRevisionBoardResponse = z.infer<
  typeof addBoardToRevisionBoardResponseSchema
>;

export const viewRevisionBoardParamsSchema = z.object({
  ticketId: z.string(),
  revisionId: z.string(),
});

export type ViewRevisionBoardParams = z.infer<
  typeof viewRevisionBoardParamsSchema
>;

export const viewRevisionBoardRequest = z.object({
  params: viewRevisionBoardParamsSchema,
});

export type ViewRevisionBoardRequest = z.infer<typeof viewRevisionBoardRequest>;

export const viewRevisionBoardResponseSchema = baseRevisionBoardSchema;

export type ViewRevisionBoardResponse = z.infer<
  typeof viewRevisionBoardResponseSchema
>;

// REVIEWS ENDPOINT
export const approveTicketParamsSchema = z.object({
  ticketId: z.string(),
});

export type ApproveTicketParams = z.infer<typeof approveTicketParamsSchema>;

export const approveTicketRequest = z.object({
  params: approveTicketParamsSchema,
});

export type ApproveTicketRequest = z.infer<typeof approveTicketRequest>;

export const approveTicketResponseSchema = baseTicketSchema;

export type ApproveTicketResponse = z.infer<typeof approveTicketResponseSchema>;

export const reviewTicketBodySchema = z.object({
  ticketId: z.string(),
  boardId: z.string(),
  description: z.string().optional(),
  approval: z.boolean(),
  creatives: z.array(z.string()).optional(),
  mentions: z.array(z.string()).optional(),
});

export type ReviewTicketBody = z.infer<typeof reviewTicketBodySchema>;

export const reviewTicketRequest = z.object({
  body: reviewTicketBodySchema,
});

export type ReviewTicketRequest = z.infer<typeof reviewTicketRequest>;

export const reviewTicketResponseSchema = baseTicketSchema;

export type ReviewTicketResponse = z.infer<typeof reviewTicketResponseSchema>;

export const addReviewerToRevisionBoardBodySchema = z.object({
  reviewers: z.array(z.string()),
  note: z.string().optional(),
});

export type AddReviewerToRevisionBoardBody = z.infer<
  typeof addReviewerToRevisionBoardBodySchema
>;

export const addReviewerToRevisionBoardParamsSchema = z.object({
  ticketId: z.string(),
});

export type AddReviewerToRevisionBoardParams = z.infer<
  typeof addReviewerToRevisionBoardParamsSchema
>;

export const addReviewerToRevisionBoardRequest = z.object({
  body: addReviewerToRevisionBoardBodySchema,
  params: addReviewerToRevisionBoardParamsSchema,
});

export type AddReviewerToRevisionBoardRequest = z.infer<
  typeof addReviewerToRevisionBoardRequest
>;

export const addReviewerToRevisionBoardResponseSchema = baseTicketSchema;

export type AddReviewerToRevisionBoardResponse = z.infer<
  typeof addReviewerToRevisionBoardResponseSchema
>;

export const removeReviewerFromRevisionBoardParamsSchema = z.object({
  ticketId: z.string(),
  reviewerId: z.string(),
});

export type RemoveReviewerFromRevisionBoardParams = z.infer<
  typeof removeReviewerFromRevisionBoardParamsSchema
>;

export const removeReviewerFromRevisionBoardRequest = z.object({
  params: removeReviewerFromRevisionBoardParamsSchema,
});

export type RemoveReviewerFromRevisionBoardRequest = z.infer<
  typeof removeReviewerFromRevisionBoardRequest
>;

export const removeReviewerFromRevisionBoardResponseSchema = baseTicketSchema;

export type RemoveReviewerFromRevisionBoardResponse = z.infer<
  typeof removeReviewerFromRevisionBoardResponseSchema
>;

// Workflow endpoints

export const getWorkflowsResponseSchema = z.array(baseWorkflowSchema);

export type GetWorkflowsResponse = z.infer<typeof getWorkflowsResponseSchema>;

// Enterprise endpoints

export const updateEnterpriseSettingsBodySchema = z
  .object({
    logo: z.string().optional(),
    extraFonts: z.array(z.string()).optional(),
  })
  .refine((data) => Object.keys(data).length > 0, {
    message: "At least one field must be provided",
  });

export type UpdateEnterpriseSettingsBody = z.infer<
  typeof updateEnterpriseSettingsBodySchema
>;

export const getBriefGuidelinesResponseSchema = enterpriseBriefGuidelines;

export type GetBriefGuidelinesResponse = z.infer<
  typeof getBriefGuidelinesResponseSchema
>;

export const getKnowledgeSettingsResponseSchema = z.object({
  guidelines: getBriefGuidelinesResponseSchema,
  aiPersonas: z.array(enterpriseAIPersonas),
  askPunttEnabled: z.boolean().optional(),
  askPunttSkills: z.array(enterpriseAskPunttSkillSchema),
  askPunttConnectors: z.array(enterpriseAskPunttConnectorSchema),
  customWorkflows: z.array(enterpriseCustomWorkflowSchema).optional(),
});

export const updateKnowledgeSettingsRequest = z.object({
  body: enterpriseBriefGuidelines,
});

export type UpdateKnowledgeSettingsRequest = z.infer<
  typeof updateKnowledgeSettingsRequest
>;

export type GetKnowledgeSettingsResponse = z.infer<
  typeof getKnowledgeSettingsResponseSchema
>;

export const updateBriefGuidelinesBodySchema = enterpriseBriefGuidelines;

export type UpdateBriefGuidelinesBody = z.infer<
  typeof updateBriefGuidelinesBodySchema
>;

export const updateBriefGuidelinesRequest = z.object({
  body: updateBriefGuidelinesBodySchema,
});

export type UpdateBriefGuidelinesRequest = z.infer<
  typeof updateBriefGuidelinesRequest
>;

export const updateBriefGuidelinesResponseSchema =
  getBriefGuidelinesResponseSchema;

export type UpdateBriefGuidelinesResponse = z.infer<
  typeof updateBriefGuidelinesResponseSchema
>;

export const getAIPersonasResponseSchema = z.array(enterpriseAIPersonas);

export type GetAIPersonasResponse = z.infer<typeof getAIPersonasResponseSchema>;

export const updateAIPersonasBodySchema = enterpriseAIPersonasUpdate;

export type UpdateAIPersonasBody = z.infer<typeof updateAIPersonasBodySchema>;

export const updateAIPersonasRequest = z.object({
  body: updateAIPersonasBodySchema,
});

export type UpdateAIPersonasRequest = z.infer<typeof updateAIPersonasRequest>;

export const updateAIPersonasResponseSchema = getAIPersonasResponseSchema;

export type UpdateAIPersonasResponse = z.infer<
  typeof updateAIPersonasResponseSchema
>;

export const updateAskPunttSkillBodySchema = z.object({
  skillId: z.string(),
  enabled: z.boolean().optional(),
  customInstructions: z.string().optional(),
  researchUrls: z.string().optional(),
});

export type UpdateAskPunttSkillBody = z.infer<
  typeof updateAskPunttSkillBodySchema
>;

export const updateAskPunttSkillRequest = z.object({
  body: updateAskPunttSkillBodySchema,
});

export const updateAskPunttSkillResponseSchema = z.array(
  enterpriseAskPunttSkillSchema,
);

export type UpdateAskPunttSkillResponse = z.infer<
  typeof updateAskPunttSkillResponseSchema
>;

export const getWarholOutputSettingsResponseSchema =
  enterpriseWarholOutputSchema;

export type GetWarholOutputSettingsResponse = z.infer<
  typeof getWarholOutputSettingsResponseSchema
>;

export const updateWarholOutputSettingsBodySchema =
  enterpriseWarholOutputSchema;

export type UpdateWarholOutputSettingsBody = z.infer<
  typeof updateWarholOutputSettingsBodySchema
>;

export const updateWarholOutputSettingsRequest = z.object({
  body: updateWarholOutputSettingsBodySchema,
});

export type UpdateWarholOutputSettingsRequest = z.infer<
  typeof updateWarholOutputSettingsRequest
>;

export const updateWarholOutputSettingsResponseSchema =
  getWarholOutputSettingsResponseSchema;

export type UpdateWarholOutputSettingsResponse = z.infer<
  typeof updateWarholOutputSettingsResponseSchema
>;

export const updateAskPunttEnabledBodySchema = z.object({
  enabled: z.boolean(),
});

export type UpdateAskPunttEnabledBody = z.infer<
  typeof updateAskPunttEnabledBodySchema
>;

export const updateAskPunttEnabledRequest = z.object({
  body: updateAskPunttEnabledBodySchema,
});

export const updateAskPunttEnabledResponseSchema = z.object({
  askPunttEnabled: z.boolean(),
});

export type UpdateAskPunttEnabledResponse = z.infer<
  typeof updateAskPunttEnabledResponseSchema
>;

export const updateAskPunttConnectorBodySchema = z.object({
  connectorId: z.string(),
  enabled: z.boolean().optional(),
});

export type UpdateAskPunttConnectorBody = z.infer<
  typeof updateAskPunttConnectorBodySchema
>;

export const updateAskPunttConnectorRequest = z.object({
  body: updateAskPunttConnectorBodySchema,
});

export const updateAskPunttConnectorResponseSchema = z.array(
  enterpriseAskPunttConnectorSchema,
);

export type UpdateAskPunttConnectorResponse = z.infer<
  typeof updateAskPunttConnectorResponseSchema
>;

// Custom Workflow CRUD

export const createCustomWorkflowBodySchema = z.object({
  name: z.string().min(1).max(100),
  trigger: z.string().min(1).max(1000),
  steps: z.string().min(1).max(5000),
  toolAccess: z.array(z.string()).optional(),
  webSearchEnabled: z.boolean().optional(),
  webSearchDomains: z.array(z.string()).optional(),
});
export type CreateCustomWorkflowBody = z.infer<
  typeof createCustomWorkflowBodySchema
>;
export const createCustomWorkflowRequest = z.object({
  body: createCustomWorkflowBodySchema,
});
export const createCustomWorkflowResponseSchema =
  enterpriseCustomWorkflowSchema;

export const updateCustomWorkflowBodySchema = z.object({
  workflowId: z.string(),
  name: z.string().min(1).max(100).optional(),
  trigger: z.string().min(1).max(1000).optional(),
  steps: z.string().min(1).max(5000).optional(),
  toolAccess: z.array(z.string()).optional(),
  webSearchEnabled: z.boolean().optional(),
  webSearchDomains: z.array(z.string()).optional(),
  enabled: z.boolean().optional(),
});
export type UpdateCustomWorkflowBody = z.infer<
  typeof updateCustomWorkflowBodySchema
>;
export const updateCustomWorkflowRequest = z.object({
  body: updateCustomWorkflowBodySchema,
});
export const updateCustomWorkflowResponseSchema = z.array(
  enterpriseCustomWorkflowSchema,
);

export const deleteCustomWorkflowBodySchema = z.object({
  workflowId: z.string(),
});
export type DeleteCustomWorkflowBody = z.infer<
  typeof deleteCustomWorkflowBodySchema
>;
export const deleteCustomWorkflowRequest = z.object({
  body: deleteCustomWorkflowBodySchema,
});
export const deleteCustomWorkflowResponseSchema = z.array(
  enterpriseCustomWorkflowSchema,
);

// Skill Document Attachment schemas

export const SKILL_DOCUMENT_ALLOWED_MIME_TYPES = [
  "application/pdf",
  "text/plain",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/png",
  "image/jpeg",
] as const;

export const uploadSkillDocumentBodySchema = z.object({
  skillId: z.string().optional(), // for built-in skills
  workflowId: z.string().optional(), // for custom workflows
  name: z.string().max(200),
  mimeType: z.enum(SKILL_DOCUMENT_ALLOWED_MIME_TYPES),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(10 * 1024 * 1024)
    .openapi({ exclusiveMinimum: 0 }), // 10MB
  description: z.string().max(500).optional(),
});
export type UploadSkillDocumentBody = z.infer<
  typeof uploadSkillDocumentBodySchema
>;
export const uploadSkillDocumentRequest = z.object({
  body: uploadSkillDocumentBodySchema,
});

export const deleteSkillDocumentBodySchema = z.object({
  skillId: z.string().optional(),
  workflowId: z.string().optional(),
  documentId: z.string().uuid(),
});
export type DeleteSkillDocumentBody = z.infer<
  typeof deleteSkillDocumentBodySchema
>;
export const deleteSkillDocumentRequest = z.object({
  body: deleteSkillDocumentBodySchema,
});

export const updateSkillRequiredInputsBodySchema = z.object({
  skillId: z.string().optional(),
  workflowId: z.string().optional(),
  requiredInputsDescription: z.string().max(2000),
});
export type UpdateSkillRequiredInputsBody = z.infer<
  typeof updateSkillRequiredInputsBodySchema
>;
export const updateSkillRequiredInputsRequest = z.object({
  body: updateSkillRequiredInputsBodySchema,
});

// Zip file endpoint

export const extractZipFileParamsSchema = z.object({
  ticketId: z.string(),
});

export type ExtractZipFileParams = z.infer<typeof extractZipFileParamsSchema>;

export const extractZipFileBodySchema = z.object({
  filesS3Keys: z.array(z.string()),
});

export type ExtractZipFileBody = z.infer<typeof extractZipFileBodySchema>;

export const extractZipFileRequest = z.object({
  params: extractZipFileParamsSchema,
  body: extractZipFileBodySchema,
});

export type ExtractZipFileRequest = z.infer<typeof extractZipFileRequest>;

export const extractZipFileResponseSchema = z.object({
  previousFiles: z.array(fileCoordSchema),
  files: z.array(fileCoordSchema),
});

export type ExtractZipFileResponse = z.infer<
  typeof extractZipFileResponseSchema
>;

// Users endpoints

export const getUsersQuerySchema = z.object({
  roles: z.array(enterpriseProfileTypeSchema).optional(),
});

export type GetUsersQuery = z.infer<typeof getUsersQuerySchema>;

export const getUsersRequest = z.object({
  query: getUsersQuerySchema,
});

export type GetUsersRequest = z.infer<typeof getUsersRequest>;

export const getUsersResponseSchema = z.array(
  z.object({
    _id: z.string(),
    name: z.string().optional(),
    avatar: z.string().optional(),
    avatarBackgroundColor: z.string().optional(),
    avatarTextColor: z.string().optional(),
    role: enterpriseProfileTypeSchema,
    email: z.string().optional(),
    archivedAt: z.string().optional(),
    archivedEmail: z.string().optional(),
    enterprise: z.union([z.string(), enterpriseSchema]).optional(),
    enterpriseProfiles: z.array(enterpriseProfileSchema).optional(),
    // Note about this user written from Manage Users, stored on the profile
    // for downstream AI use. It lives on the user's enterpriseProfiles entry
    // and is lifted to the top level here; `enterpriseProfiles` in this same
    // payload is stripped of it, so it travels once. Absent when there is no
    // note.
    adminNote: z.string().optional(),
  }),
);

export type GetUsersResponse = z.infer<typeof getUsersResponseSchema>;

export const getMeaningfulGigsUsersResponseSchema = z.array(
  z.object({
    _id: z.string(),
    name: z.string().optional(),
    avatar: z.string().optional(),
    avatarBackgroundColor: z.string().optional(),
    avatarTextColor: z.string().optional(),
    email: z.string().optional(),
    archivedAt: z.string().optional(),
  }),
);

export type GetMeaningfulGigsUsersResponse = z.infer<
  typeof getMeaningfulGigsUsersResponseSchema
>;

// PUT /v1/private/catalyst/users/{targetUserId}/admin-note
// 2-3 sentences. The cap is the shared contract for the zod validation here,
// the christo guard, and the FE textarea maxLength.
export const ADMIN_NOTE_MAX = 600;

export const updateUserAdminNoteBodySchema = z.object({
  adminNote: z.string().max(ADMIN_NOTE_MAX),
});

export type UpdateUserAdminNoteBody = z.infer<
  typeof updateUserAdminNoteBodySchema
>;

export const updateUserAdminNoteParamsSchema = z.object({
  targetUserId: z.string(),
});

// why: `validateSchema` reassigns params from the parse result, so a bare
// body schema leaves `req.params` undefined and the route 500s on
// `req.params.targetUserId` — see updateKnowledgeSourceScopeRequest above.
export const updateUserAdminNoteRequestSchema = z.object({
  body: updateUserAdminNoteBodySchema,
  params: updateUserAdminNoteParamsSchema,
});

export const updateUserAdminNoteResponseSchema = z.object({
  _id: z.string(),
  adminNote: z.string(),
});

export type UpdateUserAdminNoteResponse = z.infer<
  typeof updateUserAdminNoteResponseSchema
>;

export const reviewerProfileSummarySchema = z.object({
  userId: z.string(),
  documentCount: z.number().int().min(0),
});

export type ReviewerProfileSummary = z.infer<
  typeof reviewerProfileSummarySchema
>;

export const getReviewerProfilesResponseSchema = z.object({
  profiles: z.array(reviewerProfileSummarySchema),
});

export type GetReviewerProfilesResponse = z.infer<
  typeof getReviewerProfilesResponseSchema
>;

export const resetUserReviewerProfileRequestSchema = z.object({
  params: z.object({ targetUserId: z.string() }),
  // why: validateSchema assigns `req.query = parsedData.query`, so an
  // undeclared query strips `?removeDocuments=true` and the documents stay.
  query: z
    .object({ removeDocuments: z.enum(["true", "false"]).optional() })
    .optional()
    .default({}),
});

export const resetUserReviewerProfileResponseSchema = z.object({
  _id: z.string(),
  removedDocuments: z.number().int().min(0),
});

export type ResetUserReviewerProfileResponse = z.infer<
  typeof resetUserReviewerProfileResponseSchema
>;

export const updateUserRoleResponseSchema = z.object({
  _id: z.string(),
  name: z.string().optional(),
  avatar: z.string().optional(),
  role: enterpriseProfileTypeSchema,
  email: z.string().optional(),
});

export type UpdateUserRoleResponse = z.infer<
  typeof updateUserRoleResponseSchema
>;

export const getDownloadDeliverySchemaParams = z.object({
  ticketId: z.string(),
  boardId: z.string(),
});

export type GetDownloadDeliveryParams = z.infer<
  typeof getDownloadDeliverySchemaParams
>;

export const getDownloadDeliveryRequest = z.object({
  params: getDownloadDeliverySchemaParams,
});

export type GetDownloadDeliveryRequest = z.infer<
  typeof getDownloadDeliveryRequest
>;

export const getDownloadDeliveryResponseSchema = z.object({
  url: z.string(),
});

export type GetDownloadDeliveryResponse = z.infer<
  typeof getDownloadDeliveryResponseSchema
>;

export const sendToDesignBodySchema = z.object({
  creatives: z.array(z.string()),
  note: z.string().optional(),
});

export type SendToDesignBody = z.infer<typeof sendToDesignBodySchema>;

export const sendToDesignRequest = z.object({
  body: sendToDesignBodySchema,
});

export type SendToDesignRequest = z.infer<typeof sendToDesignRequest>;

export const sendToDesignResponseSchema = z.object({
  creatives: z.array(z.string()),
  status: z.string().optional(),
});

export type SendToDesignResponse = z.infer<typeof sendToDesignResponseSchema>;

export const suggestCreativesResponseSchema = z.object({
  suggested: getUsersResponseSchema,
  more: getUsersResponseSchema,
  network: getUsersResponseSchema,
});

export type SuggestCreativesResponse = z.infer<
  typeof suggestCreativesResponseSchema
>;

export const suggestReviewersResponseSchema = getUsersResponseSchema;

export type SuggestReviewersResponse = z.infer<
  typeof suggestReviewersResponseSchema
>;

// Internal routes

export const createEnterpriseBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  slug: enterpriseSlugSchema.optional(),
  isDemo: z.boolean().optional(),
});

export type CreateEnterpriseBody = z.infer<typeof createEnterpriseBodySchema>;

export const createEnterpriseRequest = z.object({
  body: createEnterpriseBodySchema,
});

export type CreateEnterpriseRequest = z.infer<typeof createEnterpriseRequest>;

export const createEnterpriseResponseSchema = enterpriseSchema
  .omit({
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  });

export type CreateEnterpriseResponse = z.infer<
  typeof createEnterpriseResponseSchema
>;

export const deleteEnterpriseParamsSchema = z.object({
  enterpriseId: z.string(),
});

export type DeleteEnterpriseParams = z.infer<
  typeof deleteEnterpriseParamsSchema
>;

/** Query for DELETE /internal/enterprises/:id — cascade wipe vs janitor flag. */
export const deleteEnterpriseQuerySchema = z
  .object({
    cascade: z.string().optional(),
    automation: z.string().optional(),
  })
  .passthrough();

export type DeleteEnterpriseQuery = z.infer<typeof deleteEnterpriseQuerySchema>;

export const deleteEnterpriseRequest = z.object({
  params: deleteEnterpriseParamsSchema,
  // Must declare query: validateSchema assigns `req.query = parsedData.query`.
  // Omitting it strips `?cascade=true` / `?automation=1` before the handler runs.
  query: deleteEnterpriseQuerySchema.optional().default({}),
});

export type DeleteEnterpriseRequest = z.infer<typeof deleteEnterpriseRequest>;

export const deleteEnterpriseResponseSchema = z.object({
  deleted: z.boolean(),
  enterpriseId: z.string(),
  // Present (and always `true`) only on the cascade path — the shared
  // DELETE /internal/enterprises/:id?cascade=true route returns it via
  // cascadeDeleteEnterprise. Plain deletes omit it.
  cascade: z.literal(true).optional(),
});

export type DeleteEnterpriseResponse = z.infer<
  typeof deleteEnterpriseResponseSchema
>;

/** Per-category clone toggles. Omitted keys fall back to selective defaults. */
export const duplicateEnterpriseCloneOptionsSchema = z.object({
  settings: z.boolean().optional(),
  integrations: z.boolean().optional(),
  folders: z.boolean().optional(),
  tickets: z.boolean().optional(),
  aicache: z.boolean().optional(),
  askPuntt: z.boolean().optional(),
  aiContent: z.boolean().optional(),
  knowledge: z.boolean().optional(),
  assets: z.boolean().optional(),
  userMemberships: z.boolean().optional(),
  teams: z.boolean().optional(),
  notes: z.boolean().optional(),
});

export type DuplicateEnterpriseCloneOptions = z.infer<
  typeof duplicateEnterpriseCloneOptionsSchema
>;

export const DUPLICATE_CLONE_CATEGORY_KEYS = [
  "settings",
  "integrations",
  "folders",
  "tickets",
  "aicache",
  "askPuntt",
  "aiContent",
  "knowledge",
  "assets",
  "userMemberships",
  "teams",
  "notes",
] as const;

export type DuplicateCloneCategory =
  (typeof DUPLICATE_CLONE_CATEGORY_KEYS)[number];

export type ResolvedDuplicateCloneOptions = Record<
  DuplicateCloneCategory,
  boolean
>;

/** Full clone — every category on (literal complete tenant copy). */
export const FULL_DUPLICATE_CLONE_OPTIONS: ResolvedDuplicateCloneOptions = {
  settings: true,
  integrations: true,
  folders: true,
  tickets: true,
  aicache: true,
  askPuntt: true,
  aiContent: true,
  knowledge: true,
  assets: true,
  userMemberships: true,
  teams: true,
  notes: true,
};

export const duplicateEnterpriseBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  slug: enterpriseSlugSchema.optional(),
  /** When true, clone every category (users, aicache, teams, notes, …). */
  fullClone: z.boolean().optional(),
  /** Per-category overrides when fullClone is false. */
  clone: duplicateEnterpriseCloneOptionsSchema.optional(),
  /** @deprecated Prefer `clone.integrations` or `fullClone`. */
  copyIntegrations: z.boolean().optional(),
  /** @deprecated Prefer `clone.aicache` or `fullClone`. */
  includeAicache: z.boolean().optional(),
  /** @deprecated Prefer `clone.userMemberships` or `fullClone`. */
  copyUserMemberships: z.boolean().optional(),
  skipDefaultMgRoster: z.boolean().optional(),
  isDemo: z.boolean().optional(),
  purpose: z.string().max(2000).optional(),
});

export type DuplicateEnterpriseBody = z.infer<
  typeof duplicateEnterpriseBodySchema
>;

/**
 * Selective defaults when `fullClone` is false / omitted.
 * Matches prior product behavior: content on, users/aicache/teams/notes off.
 * Integrations are opt-in — default-on copied live vendor credentials (#4488).
 */
export function selectiveDuplicateCloneDefaults(
  body: Pick<
    DuplicateEnterpriseBody,
    "copyIntegrations" | "includeAicache" | "copyUserMemberships"
  >,
): ResolvedDuplicateCloneOptions {
  return {
    settings: true,
    integrations: body.copyIntegrations === true,
    folders: true,
    tickets: true,
    aicache: body.includeAicache === true,
    askPuntt: true,
    aiContent: true,
    knowledge: true,
    assets: true,
    userMemberships: body.copyUserMemberships === true,
    teams: false,
    notes: false,
  };
}

export function resolveDuplicateCloneOptions(
  body: Pick<
    DuplicateEnterpriseBody,
    | "fullClone"
    | "clone"
    | "copyIntegrations"
    | "includeAicache"
    | "copyUserMemberships"
  >,
): ResolvedDuplicateCloneOptions {
  if (body.fullClone === true) {
    return { ...FULL_DUPLICATE_CLONE_OPTIONS };
  }
  return {
    ...selectiveDuplicateCloneDefaults(body),
    ...(body.clone ?? {}),
  };
}

export const duplicateEnterpriseParamsSchema = z.object({
  sourceEnterpriseId: z.string(),
});

export const duplicateEnterpriseRequest = z.object({
  params: duplicateEnterpriseParamsSchema,
  body: duplicateEnterpriseBodySchema,
});

export type DuplicateEnterpriseRequest = z.infer<
  typeof duplicateEnterpriseRequest
>;

export const duplicateEnterpriseResponseSchema = z.object({
  jobId: z.string(),
  targetEnterpriseId: z.string().optional(),
  status: z.enum(["queued", "running", "succeeded", "failed"]),
});

export type DuplicateEnterpriseResponse = z.infer<
  typeof duplicateEnterpriseResponseSchema
>;

export const enterpriseDuplicateJobParamsSchema = z.object({
  jobId: z.string(),
});

export const getEnterpriseDuplicateJobRequest = z.object({
  params: enterpriseDuplicateJobParamsSchema,
});

export type GetEnterpriseDuplicateJobRequest = z.infer<
  typeof getEnterpriseDuplicateJobRequest
>;

export const enterpriseDuplicateJobResponseSchema = z.object({
  jobId: z.string(),
  sourceEnterpriseId: z.string(),
  targetEnterpriseId: z.string().optional(),
  status: z.enum(["queued", "running", "succeeded", "failed"]),
  phase: z.string().optional(),
  progress: z.number().optional(),
  errorMessage: z.string().optional(),
});

export type EnterpriseDuplicateJobResponse = z.infer<
  typeof enterpriseDuplicateJobResponseSchema
>;

export const qaProvisionUserRoleSchema = z.enum([
  "admin",
  "ops",
  "meaningful-gigs",
]);

export const qaProvisionUsersBodySchema = z.object({
  users: z.array(
    z.object({
      email: z.string().email(),
      role: qaProvisionUserRoleSchema,
    }),
  ),
  sendInviteEmail: z.boolean().optional(),
});

export type QaProvisionUsersBody = z.infer<typeof qaProvisionUsersBodySchema>;

export const qaProvisionUsersParamsSchema = z.object({
  targetEnterpriseId: z.string(),
});

export const qaProvisionUsersRequest = z.object({
  params: qaProvisionUsersParamsSchema,
  body: qaProvisionUsersBodySchema,
});

export type QaProvisionUsersRequest = z.infer<typeof qaProvisionUsersRequest>;

export const qaProvisionUsersResponseSchema = z.object({
  provisioned: z.array(
    z.object({
      email: z.string(),
      userId: z.string(),
      role: qaProvisionUserRoleSchema,
      // why: prince returnToken only signs client/designer/rebel/empath —
      // callers (Nestle mint) need the wire to expose the type we persisted
      // so a regression back to illegal "user" fails the API contract.
      type: z.nativeEnum(UserType),
    }),
  ),
});

export type QaProvisionUsersResponse = z.infer<
  typeof qaProvisionUsersResponseSchema
>;

export const applyIntegrationsFromParamsSchema = z.object({
  targetEnterpriseId: z.string(),
  donorEnterpriseId: z.string(),
});

export const applyIntegrationsFromBodySchema = z.object({
  inheritOAuth: z.boolean().optional(),
  inheritedToolkits: z.array(z.string()).optional(),
  registerWebhooks: z.boolean().optional(),
});

export type ApplyIntegrationsFromBody = z.infer<
  typeof applyIntegrationsFromBodySchema
>;

export const applyIntegrationsFromRequest = z.object({
  params: applyIntegrationsFromParamsSchema,
  body: applyIntegrationsFromBodySchema.optional(),
});

export type ApplyIntegrationsFromRequest = z.infer<
  typeof applyIntegrationsFromRequest
>;

export const applyIntegrationsFromResponseSchema = z.object({
  targetEnterpriseId: z.string(),
  donorEnterpriseId: z.string(),
  applied: z.boolean(),
});

export type ApplyIntegrationsFromResponse = z.infer<
  typeof applyIntegrationsFromResponseSchema
>;

export const updateEnterpriseParamsSchema = z.object({
  enterpriseId: z.string(),
});

export type UpdateEnterpriseParams = z.infer<
  typeof updateEnterpriseParamsSchema
>;

export const updateEnterpriseBodySchema = z
  .object({
    isDemo: z.boolean().optional(),
    name: z.string().trim().min(1).max(200).optional(),
    slug: enterpriseSlugSchema.optional(),
  })
  .refine(
    (body) =>
      body.isDemo !== undefined ||
      body.name !== undefined ||
      body.slug !== undefined,
    { message: "At least one of isDemo, name, or slug is required" },
  );

export type UpdateEnterpriseBody = z.infer<typeof updateEnterpriseBodySchema>;

/** Per-enterprise SSO / auth policy update (admin + MG). */
export const updateEnterpriseAuthBodySchema = z.object({
  ssoMode: z.enum(["off", "optional", "enforced"]),
  workosOrganizationId: z.string().optional(),
  ssoDomains: z.array(z.string()).optional(),
  directorySyncEnabled: z.boolean().optional(),
  passkeysEnabled: z.boolean().optional(),
  mfaRequired: z.boolean().optional(),
  // `.min(1)`, NOT `.positive()` — see enterpriseAuthSchema.
  sessionMaxAgeDays: z.number().int().min(1).optional(),
});
export type UpdateEnterpriseAuthBody = z.infer<
  typeof updateEnterpriseAuthBodySchema
>;

export const updateEnterpriseAuthRequest = z.object({
  params: updateEnterpriseParamsSchema,
  body: updateEnterpriseAuthBodySchema,
});

export const updateEnterpriseRequest = z.object({
  params: updateEnterpriseParamsSchema,
  body: updateEnterpriseBodySchema,
});

export type UpdateEnterpriseRequest = z.infer<typeof updateEnterpriseRequest>;

/** @deprecated Use updateEnterprise* schemas */
export const updateEnterpriseIsDemoParamsSchema = updateEnterpriseParamsSchema;
/** @deprecated Use updateEnterpriseBodySchema */
export const updateEnterpriseIsDemoBodySchema = z.object({
  isDemo: z.boolean(),
});
/** @deprecated Use updateEnterpriseRequest */
export const updateEnterpriseIsDemoRequest = updateEnterpriseRequest;

/** MG-only list-all (Playwright janitor). Response = getEnterprisesResponseSchema. */
export const listAllEnterprisesRequest = z.object({
  query: z
    .object({
      automation: z.string().optional(),
    })
    .optional(),
});

export type ListAllEnterprisesRequest = z.infer<
  typeof listAllEnterprisesRequest
>;

export const listAutomationUsersRequest = z.object({
  query: z
    .object({
      automation: z.string().optional(),
    })
    .optional(),
});

export type ListAutomationUsersRequest = z.infer<
  typeof listAutomationUsersRequest
>;

export const janitorUserSummarySchema = z.object({
  _id: z.string(),
  email: z.string().optional(),
  archivedEmail: z.string().optional(),
  createdAt: z.string().optional(),
});

export const listAutomationUsersResponseSchema = z.array(
  janitorUserSummarySchema,
);

export type ListAutomationUsersResponse = z.infer<
  typeof listAutomationUsersResponseSchema
>;

export const deleteAutomationUserParamsSchema = z.object({
  userId: z.string(),
});

export const deleteAutomationUserRequest = z.object({
  params: deleteAutomationUserParamsSchema,
});

export type DeleteAutomationUserRequest = z.infer<
  typeof deleteAutomationUserRequest
>;

export const deleteAutomationUserResponseSchema = z.object({
  deleted: z.boolean(),
  userId: z.string(),
});

export type DeleteAutomationUserResponse = z.infer<
  typeof deleteAutomationUserResponseSchema
>;

export const inviteUserBodySchema = z.object({
  name: z.string(),
  // Validate the email server-side (defence-in-depth): the value flows through
  // logging, one-time-code/JWT generation, and the Mongo write before WorkOS
  // ever sees it, so reject malformed input up front. 254 is the RFC 5321 max.
  email: z.string().trim().email().max(254),
  role: enterpriseProfileTypeSchema,
});

export type InviteUserBody = z.infer<typeof inviteUserBodySchema>;

export const inviteUserParamsSchema = z.object({
  enterpriseId: z.string(),
});

export type InviteUserParams = z.infer<typeof inviteUserParamsSchema>;

export const inviteUserRequest = z.object({
  body: inviteUserBodySchema,
  params: inviteUserParamsSchema,
});

export type InviteUserRequest = z.infer<typeof inviteUserRequest>;

export const inviteUserResponseSchema = userSchema;

export type InviteUserResponse = z.infer<typeof inviteUserResponseSchema>;

// Share Ticket endpoint

export const shareTicketParamsSchema = z.object({
  ticketId: z.string(),
});

export type ShareTicketParams = z.infer<typeof shareTicketParamsSchema>;

export const shareTicketBodySchema = z.object({
  emails: z.array(z.string()).optional(),
  version: z.union([z.literal("new"), z.literal("all"), z.number()]).optional(),
});

export type ShareTicketBody = z.infer<typeof shareTicketBodySchema>;

export const shareTicketRequest = z.object({
  params: shareTicketParamsSchema,
  body: shareTicketBodySchema,
});

export type ShareTicketRequest = z.infer<typeof shareTicketRequest>;

export const shareTicketResponseSchema = z.object({
  url: z.string(),
});

export type ShareTicketResponse = z.infer<typeof shareTicketResponseSchema>;

// Share Folder endpoint

export const shareFolderParamsSchema = z.object({
  folderId: z.string(),
});

export type ShareFolderParams = z.infer<typeof shareFolderParamsSchema>;

export const shareFolderBodySchema = z.object({
  emails: z.array(z.string()).optional(),
});

export type ShareFolderBody = z.infer<typeof shareFolderBodySchema>;

export const shareFolderRequest = z.object({
  params: shareFolderParamsSchema,
  body: shareFolderBodySchema,
});

export type ShareFolderRequest = z.infer<typeof shareFolderRequest>;

export const shareFolderResponseSchema = z.object({
  url: z.string(),
});

export type ShareFolderResponse = z.infer<typeof shareFolderResponseSchema>;

// Teams endpoint

export const getTeamsResponseSchema = z.array(teamSchema);

export type GetTeamsResponse = z.infer<typeof getTeamsResponseSchema>;

export const createTeamBodySchema = teamSchema.pick({
  users: true,
  profileSettings: true,
});

export type CreateTeamBody = z.infer<typeof createTeamBodySchema>;

export const createTeamRequest = z.object({
  body: createTeamBodySchema,
});

export type CreateTeamRequest = z.infer<typeof createTeamRequest>;

export const createTeamResponseSchema = teamSchema;

export type CreateTeamResponse = z.infer<typeof createTeamResponseSchema>;

export const addUserToTeamParamsSchema = z.object({
  teamId: z.string(),
});

export type AddUserToTeamParams = z.infer<typeof addUserToTeamParamsSchema>;

export const addUserToTeamBodySchema = z.object({
  userId: z.string().optional(),
});

export type AddUserToTeamBody = z.infer<typeof addUserToTeamBodySchema>;

export const addUserToTeamRequest = z.object({
  params: addUserToTeamParamsSchema,
  body: addUserToTeamBodySchema,
});

export type AddUserToTeamRequest = z.infer<typeof addUserToTeamRequest>;

export const addUserToTeamResponseSchema = teamSchema;

export type AddUserToTeamResponse = z.infer<typeof addUserToTeamResponseSchema>;

export const removeUserFromTeamResponseSchema = teamSchema;

export type RemoveUserFromTeamResponse = z.infer<
  typeof removeUserFromTeamResponseSchema
>;

export const addTicketToTeamParamsSchema = z.object({
  teamId: z.string(),
});

export type AddTicketToTeamParams = z.infer<typeof addTicketToTeamParamsSchema>;

export const addTicketToTeamBodySchema = z.object({
  ticketId: z.string().optional(),
});

export type AddTicketToTeamBody = z.infer<typeof addTicketToTeamBodySchema>;

export const addTicketToTeamRequest = z.object({
  params: addTicketToTeamParamsSchema,
  body: addTicketToTeamBodySchema,
});

export type AddTicketToTeamRequest = z.infer<typeof addTicketToTeamRequest>;

export const addTicketToTeamResponseSchema = baseTicketSchema;

export type AddTicketToTeamResponse = z.infer<
  typeof addTicketToTeamResponseSchema
>;

export const removeTicketFromTeamResponseSchema = baseTicketSchema;

export type RemoveTicketFromTeamResponse = z.infer<
  typeof removeTicketFromTeamResponseSchema
>;

export const getParticipantsResponseSchema = ticketParticipantsSchema;

export type GetParticipantsResponse = z.infer<
  typeof getParticipantsResponseSchema
>;

// Slim shape used by the enterprise switcher (sidebar / drawer / chrome ext / error
// boundary). Only fields that are actually rendered. Returning the full Enterprise
// document used to push the response past Lambda's 6 MB sync-invoke limit for users
// who belong to many enterprises, causing a deterministic 502 from API Gateway.
//
// `isDemo` is also included because the frontend's `isDemoEnterprise()` gate
// (frontends/carrie/src/utils/auth.ts) reads this list synchronously to decide
// which surfaces (Vitalora home, Agents, Integrations, ROI sidebar link) to
// render. Dropping the field made every demo user fall through to the
// non-demo redirect — `/` bounced to `/ask-puntt` instead of `/home`. It's a
// single boolean per enterprise, so the projection cost is negligible
// compared to the originally-removed `integrations` / `aiPersonas` blobs.
export const enterpriseSummarySchema = z.object({
  _id: z.string(),
  name: z.string(),
  slug: z.string().optional(),
  logo: z.string().optional(),
  isDemo: z.boolean().optional(),
  /** Slim SSO mode for switcher re-auth UX (full auth via GET .../auth). */
  auth: z
    .object({
      ssoMode: z.enum(["off", "optional", "enforced"]).optional(),
    })
    .optional(),
});

export type EnterpriseSummary = z.infer<typeof enterpriseSummarySchema>;

export const getEnterprisesResponseSchema = z.array(enterpriseSummarySchema);

export type GetEnterprisesResponse = z.infer<
  typeof getEnterprisesResponseSchema
>;

export const combineTicketsBodySchema = z.object({
  receivingTicketId: z.string(),
  addingTicketId: z.string(),
});

export type CombineTicketsBody = z.infer<typeof combineTicketsBodySchema>;

export const combineTicketsRequest = z.object({
  body: combineTicketsBodySchema,
});

export type CombineTicketsRequest = z.infer<typeof combineTicketsRequest>;

// ---------------------------------------------------------------------------
// Knowledge Database (refresh): sources, rules, activity under /knowledge/*.
// DTO dates are ISO strings; server derives trustLevel and reconstructs the
// s3Key — neither is ever client input.
// ---------------------------------------------------------------------------
export const knowledgeScopeDtoSchema = knowledgeScopeSchema;

export const updateKnowledgeScopeBodySchema = z.object({
  brand: z.string().max(200).nullable().optional(),
  region: z.string().max(200).nullable().optional(),
  team: z.string().max(200).nullable().optional(),
});

export type UpdateKnowledgeScopeBody = z.infer<
  typeof updateKnowledgeScopeBodySchema
>;

export const updateKnowledgeSourceScopeParamsSchema = z.object({
  sourceId: z.string(),
});

export const updateKnowledgeRuleScopeParamsSchema = z.object({
  ruleId: z.string(),
});

/**
 * `validateSchema` parses the envelope `{ body, query, params }` and then
 * reassigns ALL THREE onto the request from the parse result. A bare body
 * schema therefore leaves `req.params` undefined and the scope routes throw on
 * `req.params.sourceId` / `req.params.ruleId`, so both wrappers MUST declare
 * `params` alongside `body`.
 */
export const updateKnowledgeSourceScopeRequest = z.object({
  body: updateKnowledgeScopeBodySchema,
  params: updateKnowledgeSourceScopeParamsSchema,
});

export type UpdateKnowledgeSourceScopeRequest = z.infer<
  typeof updateKnowledgeSourceScopeRequest
>;

export const updateKnowledgeRuleScopeRequest = z.object({
  body: updateKnowledgeScopeBodySchema,
  params: updateKnowledgeRuleScopeParamsSchema,
});

export type UpdateKnowledgeRuleScopeRequest = z.infer<
  typeof updateKnowledgeRuleScopeRequest
>;

/**
 * Human usage note on a source ("cite this only for EU packaging"). `null` or
 * a blank string clears it. Paired with the AI-extracted `description` when a
 * rule's source is rendered into the retrieval prompt.
 */
export const updateKnowledgeSourceNoteBodySchema = z.object({
  note: z.string().max(4000).nullable(),
});

export type UpdateKnowledgeSourceNoteBody = z.infer<
  typeof updateKnowledgeSourceNoteBodySchema
>;

/** Declares `params` for the same reason the scope wrappers do — see above. */
export const updateKnowledgeSourceNoteRequest = z.object({
  body: updateKnowledgeSourceNoteBodySchema,
  params: updateKnowledgeSourceScopeParamsSchema,
});

export type UpdateKnowledgeSourceNoteRequest = z.infer<
  typeof updateKnowledgeSourceNoteRequest
>;

export const createKnowledgeSourcePresignedUrlBodySchema = z.object({
  fileName: z.string().min(1).max(512),
  mimeType: z.string().min(1).max(256),
  sizeBytes: z.coerce
    .number()
    .int()
    .min(1)
    .max(100 * 1024 * 1024),
});

export type CreateKnowledgeSourcePresignedUrlBody = z.infer<
  typeof createKnowledgeSourcePresignedUrlBodySchema
>;

export const createKnowledgeSourcePresignedUrlRequest = z.object({
  body: createKnowledgeSourcePresignedUrlBodySchema,
});

export const createKnowledgeSourcePresignedUrlResponseSchema = z.object({
  url: z.string(),
  fields: z.record(z.string()),
  s3Key: z.string(),
  sourceId: z.string(),
});

export type CreateKnowledgeSourcePresignedUrlResponse = z.infer<
  typeof createKnowledgeSourcePresignedUrlResponseSchema
>;

export const registerKnowledgeSourceBodySchema = z.object({
  sourceId: z.string().min(1),
  fileName: z.string().min(1).max(512),
  mimeType: z.string().min(1).max(256),
  sizeBytes: z.coerce
    .number()
    .int()
    .min(1)
    .max(100 * 1024 * 1024),
  sha256: z.string().optional(),
  scope: updateKnowledgeScopeBodySchema.optional(),
});

export type RegisterKnowledgeSourceBody = z.infer<
  typeof registerKnowledgeSourceBodySchema
>;

export const registerKnowledgeSourceRequest = z.object({
  body: registerKnowledgeSourceBodySchema,
});

/**
 * The Knowledge Database's graph: when warhol last finished building it, how
 * big it is, and whether a build is running now. `builtAt`/`nodes`/`edges` are
 * null until the first build that records them.
 */
export const knowledgeGraphStatusSchema = z.object({
  /** A build has finished. True with a null `builtAt` for a graph built before warhol recorded the time. */
  built: z.boolean(),
  builtAt: z.string().datetime().nullable(),
  nodes: z.number().int().nonnegative().nullable(),
  edges: z.number().int().nonnegative().nullable(),
  building: z.boolean(),
});
export type KnowledgeGraphStatus = z.infer<typeof knowledgeGraphStatusSchema>;

export const knowledgeSourceResultSchema = z.object({
  _id: z.string(),
  sourceType: z.nativeEnum(KnowledgeSourceType),
  trustLevel: z.nativeEnum(KnowledgeTrustLevel),
  file: z.object({
    fileName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number(),
  }),
  status: z.nativeEnum(KnowledgeSourceStatus),
  error: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  scope: knowledgeScopeDtoSchema,
  scopeSource: z.nativeEnum(KnowledgeScopeSource),
  textStats: z
    .object({
      pageCount: z.number().nullable().optional(),
      sheetCount: z.number().nullable().optional(),
      charCount: z.number().nullable().optional(),
    })
    .nullable()
    .optional(),
  extraction: z
    .object({
      status: z.nativeEnum(KnowledgeExtractionStatus),
      error: z.string().nullable().optional(),
      ruleCount: z.number().nullable().optional(),
      completedAt: z.string().datetime().nullable().optional(),
    })
    .nullable()
    .optional(),
  // What produced the source when no person uploaded it here, e.g. the chat a
  // spec was attached in. Null for a manual upload.
  interactionRef: z
    .object({ kind: z.string(), id: z.string() })
    .nullable()
    .optional(),
  createdBy: z.string(),
  processedAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type KnowledgeSourceResult = z.infer<typeof knowledgeSourceResultSchema>;

export const getKnowledgeSourcesQueryParams = z.object({
  status: z.nativeEnum(KnowledgeSourceStatus).optional(),
  pageSize: z.coerce.number().min(1).max(200).default(50).optional(),
  searchAfter: z.string().optional(),
});

export type GetKnowledgeSourcesQueryParams = z.infer<
  typeof getKnowledgeSourcesQueryParams
>;

export const getKnowledgeSourcesResponseSchema = z.object({
  sources: z.array(knowledgeSourceResultSchema),
  more: z.boolean(),
  nextCursor: z.string().nullable(),
});

export type GetKnowledgeSourcesResponse = z.infer<
  typeof getKnowledgeSourcesResponseSchema
>;

export const getKnowledgeSourceDownloadResponseSchema = z.object({
  url: z.string(),
  fileName: z.string(),
  mimeType: z.string(),
});

export type GetKnowledgeSourceDownloadResponse = z.infer<
  typeof getKnowledgeSourceDownloadResponseSchema
>;

export const knowledgeSourceParamsSchema = z.object({
  sourceId: z.string(),
});

export const knowledgeRuleCitationResultSchema = z.object({
  sourceId: z.string(),
  extractionId: z.string().nullable().optional(),
  trustLevel: z.nativeEnum(KnowledgeTrustLevel),
  quote: z.string(),
  location: knowledgeCitationLocationSchema,
  // Ranking fields (null on citations synthesized before they existed):
  // extraction confidence, relevance of this citation to its rule, and when
  // the extraction ran — the UI leads with the most recent high-trust
  // source's most relevant citation.
  confidence: z.number().nullable().optional(),
  similarity: z.number().nullable().optional(),
  extractedAt: z.string().datetime().nullable().optional(),
});

export type KnowledgeRuleCitationResult = z.infer<
  typeof knowledgeRuleCitationResultSchema
>;

export const knowledgeRuleResultSchema = z.object({
  _id: z.string(),
  title: z.string().nullable().optional(),
  content: z.string(),
  category: z.nativeEnum(KnowledgeRuleCategory),
  status: z.nativeEnum(KnowledgeRuleStatus),
  origin: z.nativeEnum(KnowledgeRuleOrigin),
  confidence: z.number(),
  scope: knowledgeScopeDtoSchema,
  scopeSource: z.nativeEnum(KnowledgeScopeSource),
  citations: z.array(knowledgeRuleCitationResultSchema),
  usage: z.object({
    reviewCount: z.number(),
    lastUsedInReviewAt: z.string().datetime().nullable().optional(),
  }),
  confirmationCount: z.number(),
  statusChangedBy: z.string().nullable().optional(),
  statusChangedAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Engineered rule features served to the UI so it can show them as separate
  // fields (claim polarity/conditions, tag dims, lossless source columns)
  // rather than re-parsing the raw source sheet. Additive + optional.
  claim: knowledgeRuleClaimSchema.optional(),
  tags: knowledgeRuleTagsSchema.optional(),
  sourceFields: z.record(z.string(), z.string()).optional(),
});

export type KnowledgeRuleResult = z.infer<typeof knowledgeRuleResultSchema>;

export const getKnowledgeRulesQueryParams = z.object({
  status: z.nativeEnum(KnowledgeRuleStatus).optional(),
  category: z.nativeEnum(KnowledgeRuleCategory).optional(),
  query: z.string().optional(),
  sourceId: z.string().optional(),
  pageSize: z.coerce.number().min(1).max(200).default(50).optional(),
  searchAfter: z.string().optional(),
});

export type GetKnowledgeRulesQueryParams = z.infer<
  typeof getKnowledgeRulesQueryParams
>;

export const getKnowledgeRulesResponseSchema = z.object({
  rules: z.array(knowledgeRuleResultSchema),
  statusCounts: z.record(z.number()),
  // Per-category counts across the scoped set (ignoring the active category
  // filter) — feeds the Rules tab category chips. `.default({})` keeps older
  // API responses (and load-more pages) parseable.
  categoryCounts: z.record(z.number()).default({}),
  more: z.boolean(),
  nextCursor: z.string().nullable(),
});

export type GetKnowledgeRulesResponse = z.infer<
  typeof getKnowledgeRulesResponseSchema
>;

export const knowledgeRuleParamsSchema = z.object({
  ruleId: z.string(),
});

export const knowledgeActivityResultSchema = z.object({
  _id: z.string(),
  actor: z.object({
    kind: z.nativeEnum(KnowledgeActivityActorKind),
    userId: z.string().nullable().optional(),
    workflow: z.string().nullable().optional(),
  }),
  action: z.nativeEnum(KnowledgeActivityAction),
  target: z.object({
    type: z.nativeEnum(KnowledgeActivityTargetType),
    id: z.string(),
    label: z.string().nullable().optional(),
  }),
  createdAt: z.string().datetime(),
});

export type KnowledgeActivityResult = z.infer<
  typeof knowledgeActivityResultSchema
>;

export const getKnowledgeActivityQueryParams = z.object({
  targetType: z.nativeEnum(KnowledgeActivityTargetType).optional(),
  targetId: z.string().optional(),
  pageSize: z.coerce.number().min(1).max(200).default(50).optional(),
  searchAfter: z.string().optional(),
});

export type GetKnowledgeActivityQueryParams = z.infer<
  typeof getKnowledgeActivityQueryParams
>;

export const getKnowledgeActivityResponseSchema = z.object({
  activities: z.array(knowledgeActivityResultSchema),
  more: z.boolean(),
  nextCursor: z.string().nullable(),
});

export type GetKnowledgeActivityResponse = z.infer<
  typeof getKnowledgeActivityResponseSchema
>;

// ---------------------------------------------------------------------------
// Enterprise memories (read-only view of the rembrandt-owned
// `enterprise_memories` collection). The christo handler maps the raw
// snake_case docs to this clean DTO and NORMALIZES the date strings — the
// source dates have inconsistent timezone offsets and a single invalid date
// would otherwise blank the whole tab (parseResponse rethrows on any ZodError).
// ---------------------------------------------------------------------------
export const enterpriseMemoryScope = z.object({
  brand: z.string().nullable(),
  region: z.string().nullable(),
  team: z.string().nullable(),
  projectId: z.string().nullable(),
});

export const enterpriseMemoryResult = z.object({
  id: z.string(),
  type: z.string(),
  content: z.string(),
  context: z.string().nullable(),
  scope: enterpriseMemoryScope,
  confirmationCount: z.number(),
  occurredAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime(),
});

export type EnterpriseMemoryResult = z.infer<typeof enterpriseMemoryResult>;

export const enterpriseMemoryTypeCount = z.object({
  type: z.string(),
  count: z.number(),
});

export const getEnterpriseMemoriesResponseSchema = z.object({
  memories: z.array(enterpriseMemoryResult),
  typeCounts: z.array(enterpriseMemoryTypeCount),
  more: z.boolean(),
  nextCursor: z.string().nullable(),
});

export type GetEnterpriseMemoriesResponse = z.infer<
  typeof getEnterpriseMemoriesResponseSchema
>;

export const getEnterpriseMemoriesQueryParams = z.object({
  pageSize: z.coerce.number().default(100).optional(),
  searchAfter: z.string().optional(),
  query: z.string().optional(),
  type: z.string().optional(),
});

export type GetEnterpriseMemoriesQueryParams = z.infer<
  typeof getEnterpriseMemoriesQueryParams
>;

export const createTicketRedlineBodySchema = redlinesSchema;

export type CreateTicketRedlineBody = z.infer<
  typeof createTicketRedlineBodySchema
>;

export const createTicketRedlineRequest = z.object({
  body: createTicketRedlineBodySchema,
});
export type GetRedlinesRequestSchema = z.infer<
  typeof createTicketRedlineRequest
>;

export const summarizeIntegrationsRequestSchema = z.object({
  ticketId: z.string(),
  action: z.enum(["accept", "reject"]),
});

export const summarizeIntegrationsRequest = z.object({
  body: summarizeIntegrationsRequestSchema,
});

export const summarizeIntegrationsResponseSchema = z.object({
  summary: z.array(z.string()),
});

export type SummarizeIntegrationsResponse = z.infer<
  typeof summarizeIntegrationsResponseSchema
>;

export const submitSummaryRequestSchema = z.object({
  adjustedSummary: z.array(z.string()).nullish(),
  ticketId: z.string(),
  action: z.string().optional(),
});

export const submitSummaryRequest = z.object({
  body: submitSummaryRequestSchema,
});

// ============================================
// Audit Log Schemas
// ============================================

export const missedIssueSchema = z.object({
  atomic_task: z.string(), // "AT1", "AT2", ..., "AT10", or "UNKNOWN"
  comment_text: z.string(),
  detection_category: z.string(), // "Objective", "Semi-Objective", "Subjective"
});

export type MissedIssue = z.infer<typeof missedIssueSchema>;

export const auditLogStatsSchema = z.object({
  ticket_ids: z.array(z.string()),
  cycle_start: z.string().datetime().nullable(),
  cycle_end: z.string().datetime().nullable(),
  atomic_tasks: z.array(z.string()), // "AT1", "AT2", ..., "AT10"
  missed_issue_str: z.record(z.string(), z.unknown()).optional(),
  missed_issues: z.array(missedIssueSchema),
  missed_issue_count: z.number(),
  total_issues: z.number(),
  already_caught_count: z.number(),
});

export type AuditLogStats = z.infer<typeof auditLogStatsSchema>;

export const auditLogLastErrorSchema = z.object({
  message: z.string(),
  error_type: z.string().optional(),
  attempt: z.number().optional(),
  timestamp: z.string().datetime().optional(),
  permalink: z.string().optional(),
});

export type AuditLogLastError = z.infer<typeof auditLogLastErrorSchema>;

export const auditLogEntrySchema = z.object({
  _id: z.string(),
  composite_task_id: z.string().optional(),
  enterprise_id: z.string(),
  task_id: z.string().optional(),
  task_name: z.string().optional(),
  wrike_permalink: z.string().optional(),
  event_type: z.enum(["wrike", "asana"]),
  state: z.string().optional(),
  // Populated when state="retrying" or "failed". Carries the most recent
  // failure so the UI can surface "transient outage, retrying (attempt 3)"
  // instead of nothing.
  last_error: auditLogLastErrorSchema.optional(),
  stats: auditLogStatsSchema.optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type AuditLogEntry = z.infer<typeof auditLogEntrySchema>;

export const getAuditLogQuerySchema = z.object({
  pageSize: z.coerce.number().default(50).optional(),
  page: z.coerce.number().default(1).optional(),
  // Date range filter for cycle_start
  startDate: z.string().optional(), // ISO date string
  endDate: z.string().optional(), // ISO date string
  // Array of atomic task IDs to filter by (e.g., [1, 3, 5] or ["AT1", "AT3", "AT5"])
  atomicTasks: z
    .union([z.string(), z.array(z.string())])
    .transform((val) => (Array.isArray(val) ? val : [val]))
    .optional(),
});

export type GetAuditLogQuery = z.infer<typeof getAuditLogQuerySchema>;

export const getAuditLogResponseSchema = z.object({
  entries: z.array(auditLogEntrySchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  hasMore: z.boolean(),
});

export type GetAuditLogResponse = z.infer<typeof getAuditLogResponseSchema>;

// ============================================
// Grouped Audit Log Schemas (collapsed view)
// ============================================

// Individual cycle data for expansion
export const auditLogCycleSchema = z.object({
  ticketIds: z.array(z.string()),
  ticketTitles: z.array(z.string()).optional(),
  cycleStart: z.string().datetime().nullable(),
  cycleEnd: z.string().datetime().nullable(),
  atomicTasks: z.array(z.string()),
  missedIssues: z.array(missedIssueSchema),
  missedIssueCount: z.number(),
  totalIssues: z.number(),
  alreadyCaughtCount: z.number(),
});

export type AuditLogCycle = z.infer<typeof auditLogCycleSchema>;

// Grouped entry (one per Wrike task)
export const groupedAuditLogEntrySchema = z.object({
  taskId: z.string(),
  taskName: z.string(),
  wrikePermalink: z.string().optional(),
  // Operational state of the underlying IntegrationEvent: "completed" | "failed"
  // | "running" | "pending" | "retrying" etc. Optional so older callers don't
  // break; needed for the UI to differentiate failed/in-flight runs from
  // successful ones when no AI-review stats are populated.
  state: z.string().optional(),
  // Last failure for retrying / failed rows. Lets the UI render
  // "transient outage, retrying (attempt 3) — Invalid API key …".
  lastError: auditLogLastErrorSchema.optional(),
  // Aggregated stats
  firstCycleStart: z.string().datetime().nullable(),
  reviewCycleCount: z.number(),
  totalAtomicTasks: z.number(), // Count of unique atomic tasks
  totalHumanIssues: z.number(), // Deduplicated count: at most one per ticketId:atomicTask
  // Nested cycles for expansion
  cycles: z.array(auditLogCycleSchema),
});

export type GroupedAuditLogEntry = z.infer<typeof groupedAuditLogEntrySchema>;

export const getGroupedAuditLogResponseSchema = z.object({
  entries: z.array(groupedAuditLogEntrySchema),
  total: z.number(), // Total Wrike tasks matching filter
  page: z.number(),
  pageSize: z.number(),
  hasMore: z.boolean(),
});

export type GetGroupedAuditLogResponse = z.infer<
  typeof getGroupedAuditLogResponseSchema
>;

// ============================================
// Dashboard Stats Schemas
// ============================================

export const dashboardTaskStatsSchema = z.object({
  categoryId: z.string(),
  categoryName: z.string().optional(), // Resolved from category lookup
  errorFreeChecks: z.number(),
  humanCaught: z.number(),
  totalChecks: z.number(),
  aiReviewSpeed: z.number(), // In seconds
  auditLog: z.number(),
});

export type DashboardTaskStats = z.infer<typeof dashboardTaskStatsSchema>;

export const dashboardStatsSchema = z.object({
  _id: z.string().optional(),
  enterprise_id: z.string(),
  automationPercentage: z.number(),
  breakdownNumbers: z.object({
    full: z.number(),
    training: z.number(),
    outOfScope: z.number(),
  }),
  fullyAutomated: z.array(dashboardTaskStatsSchema),
  inTraining: z.array(dashboardTaskStatsSchema),
  outOfScope: z.array(dashboardTaskStatsSchema),
  // Date range this stats snapshot covers
  periodStart: z.string().datetime().nullable(),
  periodEnd: z.string().datetime().nullable(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});

export type DashboardStats = z.infer<typeof dashboardStatsSchema>;

export const getDashboardStatsQuerySchema = z.object({
  startDate: z.string().optional(), // ISO date string
  endDate: z.string().optional(), // ISO date string
});

export type GetDashboardStatsQuery = z.infer<
  typeof getDashboardStatsQuerySchema
>;

export const getDashboardStatsResponseSchema = dashboardStatsSchema;

export type GetDashboardStatsResponse = z.infer<
  typeof getDashboardStatsResponseSchema
>;

// ============================================
// ROI Dashboard Schemas (real-time computed)
// ============================================

export const roiDashboardCategoryResultSchema = z.object({
  categoryId: z.string(),
  categoryName: z.string().optional(),
  totalChecks: z.number(),
  errorFreeChecks: z.number(),
  humanCaughtLowRisk: z.number(),
  humanCaughtHighRisk: z.number(),
  aiReviewSpeed: z.number(),
  auditLog: z.number(),
  status: z.enum(["Fully Automated", "In Training", "Out of Scope"]),
  weeksWithoutHighRisk: z.number().nullable(),
});

export type RoiDashboardCategoryResult = z.infer<
  typeof roiDashboardCategoryResultSchema
>;

export const getRoiDashboardQuerySchema = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

export type GetRoiDashboardQuery = z.infer<typeof getRoiDashboardQuerySchema>;

export const auditSummaryStatsSchema = z.object({
  totalWrikeTasks: z.number(),
  totalReviewCycles: z.number(),
  totalTaskChecks: z.number(),
  dateRangeStart: z.string().datetime().nullable(),
  dateRangeEnd: z.string().datetime().nullable(),
  // The next block is added for the demo headline strip (S11). Real
  // tenants currently leave these undefined so the strip falls back to
  // the existing summary cards. Demo tenants get hardcoded values that
  // match the sales deck so the screenshots align.
  cycleTimeReduction: z.number().min(0).max(1).optional(),
  revisionsBefore: z.number().optional(),
  revisionsAfter: z.number().optional(),
  aiAccuracy: z.number().min(0).max(1).optional(),
  estimatedSavings: z.number().optional(),
  avgTurnaroundHrs: z.number().optional(),
  marketsCovered: z.number().optional(),
  reviewsThisQuarter: z.number().optional(),
});

export type AuditSummaryStats = z.infer<typeof auditSummaryStatsSchema>;

export const getRoiDashboardResponseSchema = z.object({
  automationPercentage: z.number().min(0).max(100),
  breakdownNumbers: z.object({
    full: z.number(),
    training: z.number(),
    outOfScope: z.number(),
  }),
  results: z.array(roiDashboardCategoryResultSchema),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  summaryStats: auditSummaryStatsSchema.optional(),
});

export type GetRoiDashboardResponse = z.infer<
  typeof getRoiDashboardResponseSchema
>;
